import { useEffect, useMemo, useState } from "react";
import PageHeader from "@/components/PageHeader";
import { Reveal } from "@/components/Reveal";
import { IMG } from "@/lib/images";
import { useSeo } from "@/lib/seo";
import { track } from "@/lib/track";

const MENU = [
  {
    cat: "Ice Creams",
    groups: [
      {
        label: "Premium",
        items: [
          { name: "Vanilla" },
          { name: "Chocolate" },
          { name: "Strawberry", ripe: true },
          { name: "Mango", ripe: true },
        ],
      },
      {
        label: "Originals",
        items: [
          { name: "Malai" },
          { name: "Jelly" },
          { name: "Gulkand" },
          { name: "Tutti Frutti" },
          { name: "Plain Coffee" },
          { name: "Coffee Butterscotch" },
        ],
      },
      {
        label: "Orchard",
        items: [
          { name: "Alphonso Mango", ripe: true },
          { name: "Chikoo", ripe: true },
          { name: "Guava", ripe: true },
          { name: "Jackfruit", ripe: true },
          { name: "Lychee", ripe: true },
          { name: "Pineapple" },
          { name: "Sitaphal", ripe: true },
          { name: "Tender Coconut" },
        ],
      },
      {
        label: "Dark",
        items: [
          { name: "Chocolate Chips" },
          { name: "Chocolate Almond" },
          { name: "Plain Chocolate" },
        ],
      },
      {
        label: "Reserve",
        items: [
          { name: "Caramel" },
          { name: "Fig & Honey" },
          { name: "Mixed Dry Fruits" },
          { name: "Roasted Almond" },
          { name: "Butterscotch" },
          { name: "Anjeer" },
          { name: "Coffee Walnut" },
        ],
      },
    ],
  },
  {
    cat: "Sundaes",
    items: [
      { name: "Hot Chocolate Fudge" },
      { name: "Dry Fruits Overloaded" },
      { name: "Brownie Fudge" },
      { name: "Death By Chocolate" },
      { name: "GudBud Classic" },
      { name: "Brownie with Ice Cream" },
    ],
  },
  {
    cat: "Waffles",
    items: [
      { name: "Butterscotch" },
      { name: "Dark Chocolate" },
      { name: "White Chocolate" },
      { name: "Biscoff" },
      { name: "KitKat" },
    ],
  },
  {
    cat: "Milkshakes",
    items: [
      { name: "Mango", ripe: true },
      { name: "Sitaphal", ripe: true },
      { name: "Tender Coconut" },
      { name: "Chocolate" },
      { name: "Strawberry", ripe: true },
    ],
  },
  {
    cat: "Thickshakes",
    items: [
      { name: "Mango", ripe: true },
      { name: "Sitaphal", ripe: true },
      { name: "Strawberry", ripe: true },
      { name: "Oreo" },
      { name: "KitKat" },
      { name: "Brownie" },
      { name: "Chocolate" },
      { name: "Anjeer" },
      { name: "Chikoo", ripe: true },
    ],
  },
  {
    cat: "Lassi",
    items: [
      { name: "Kulhad Classic" },
      { name: "Mango", ripe: true },
      { name: "Strawberry", ripe: true },
      { name: "Rose" },
      { name: "Mini Classic" },
    ],
  },
  {
    cat: "Coolers",
    items: [
      { name: "Fresh Lime Mint" },
      { name: "Raw Mango Mint", ripe: true },
      { name: "Guava Chilli", ripe: true },
      { name: "Kala Khatta" },
      { name: "Watermelon Mint", ripe: true },
    ],
  },
];

const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-");
const countFor = (s) => (s.groups ? s.groups.reduce((n, g) => n + g.items.length, 0) : s.items.length);

const RipeTag = ({ tid }) => (
  <span
    data-testid={tid}
    className="mt-2 inline-flex items-center gap-1.5 font-mono text-[10px] font-medium uppercase tracking-[0.16em] text-orange-rific"
  >
    <span className="h-1.5 w-1.5 rounded-full bg-orange-rific" />
    Ripe Now
  </span>
);

export default function Menu() {
  useSeo({
    title: "Menu | RIFIC — Ice Creams, Sundaes & Shakes",
    description:
      "Explore the full RIFIC menu — ice creams across Premium, Originals, Orchard, Dark and Reserve, plus sundaes, waffles, milkshakes, thickshakes, lassi and coolers. Seasonal RIPE NOW flavours follow the fruit calendar.",
    path: "/menu",
  });

  useEffect(() => track("menu_view"), []);

  const [active, setActive] = useState("All");
  const sections = useMemo(
    () => (active === "All" ? MENU : MENU.filter((s) => s.cat === active)),
    [active]
  );

  return (
    <div data-testid="menu-page">
      <PageHeader
        eyebrow="The Menu"
        title={["SCOOPS TO", "SHAKES."]}
        copy="Ice Creams • Sundaes • Waffles • Milkshakes • Thickshakes • Lassi • Coolers — real fruit, real milk and real ingredients, served at your nearest RIFIC store."
        img={IMG.heroMaster}
      />

      <section className="bg-cream pb-16 md:pb-24">
        <div className="mx-auto max-w-[1440px] px-5 md:px-10">
          <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-none md:flex-wrap" data-testid="menu-filters">
            {["All", ...MENU.map((s) => s.cat)].map((c) => (
              <button
                key={c}
                data-testid={`menu-filter-${slug(c)}`}
                onClick={() => setActive(c)}
                className={`h-11 shrink-0 rounded-full px-6 text-xs font-bold uppercase tracking-[0.14em] transition-colors duration-300 ${
                  active === c ? "bg-deep text-cream" : "border border-deep/15 bg-white text-deep hover:border-orange-rific hover:text-orange-rific"
                }`}
              >
                {c}
              </button>
            ))}
          </div>

          <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.16em] text-deep/45 md:text-[11px]" data-testid="menu-ripe-legend">
            <span className="mr-2 inline-block h-1.5 w-1.5 rounded-full bg-orange-rific align-middle" />
            Ripe Now — seasonal fruit flavours that follow the fruit calendar
          </p>

          <div className="mt-10 md:mt-14" data-testid="menu-grid">
            {sections.map((s) => (
              <div key={s.cat} id={`cat-${slug(s.cat)}`} className="scroll-mt-28 border-t border-cream-line py-10 first:border-t-0 md:py-14">
                <Reveal>
                  <div className="flex items-baseline justify-between gap-4">
                    <h2 className="font-display text-[clamp(1.5rem,2.2vw,1.875rem)] font-extrabold uppercase tracking-[-0.01em] text-deep">
                      {s.cat}
                    </h2>
                    <span className="font-mono text-xs text-deep/45">
                      {String(MENU.indexOf(s) + 1).padStart(2, "0")} — {countFor(s)} flavours
                    </span>
                  </div>
                </Reveal>
                <div className="mt-8">
                  {(s.groups ?? [{ items: s.items }]).map((g) => (
                    <div key={g.label ?? s.cat} className={g.label ? "mt-10 first:mt-0 md:mt-12" : ""}>
                      {g.label && (
                        <Reveal>
                          <p className="font-mono text-[11px] font-medium uppercase tracking-[0.28em] text-orange-rific">{g.label}</p>
                        </Reveal>
                      )}
                      <div className="grid gap-x-8 sm:grid-cols-2 lg:grid-cols-3">
                        {g.items.map((m) => (
                          <Reveal key={`${s.cat}-${g.label ?? ""}-${m.name}`}>
                            <div
                              data-testid={`menu-item-${slug(s.cat)}-${slug(m.name)}`}
                              className="group border-t border-cream-line py-4"
                            >
                              <h3 className="text-base font-bold text-deep transition-colors duration-300 group-hover:text-orange-rific md:text-lg">
                                {m.name}
                              </h3>
                              {m.ripe && <RipeTag tid={`menu-item-${slug(s.cat)}-${slug(m.name)}-ripe-badge`} />}
                            </div>
                          </Reveal>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <p className="mt-8 text-center text-base text-deep/55" data-testid="menu-disclaimer">
            The line-up varies a little by store and season — walk in to see what's churning today.
          </p>
        </div>
      </section>
    </div>
  );
}
